import { FormControl, makeStyles } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import PropTypes from "prop-types";

const allowedInputTypes = ["text", "email", "password", "search", "tel", "url"];

const fontSize = 14;
const inputHeight = 52;
const borderWidth = 1;

const useStyles = makeStyles({
    formControl: {
        position: "relative",
        width: "100%",
    },
    inputLabel: {
        fontSize: fontSize,
        color: "#6b7280",
        position: "absolute",
        top: inputHeight / 2 - fontSize + 2,
        left: 10,
        transition: "100ms",
        cursor: "text",

        "&.inputFilled": {
            top: inputHeight / 2 - fontSize * 2 + 6,
            fontSize: fontSize - 4,
        },
    },
    requiredMark: {
        color: "#d32f2f",

        "& svg": {
            width: fontSize / 3,
            position: "relative",
            left: "2px",
            bottom: "4px",
            height: fontSize,
        },
    },
    inputDisplay: {
        height: inputHeight,
        paddingTop: (inputHeight - fontSize) / 2 - borderWidth * 2,
        paddingBottom: (inputHeight - fontSize * 2) / 2 - borderWidth * 2,
        paddingLeft: 10,
        paddingRight: 10,
        fontSize: fontSize,
        backgroundColor: "#ffffff",
        border: `${borderWidth}px solid #d1d5db`,
        borderRadius: "4px",
        cursor: "text",
        transition: "300ms",
        outline: "none",
        width: "100%",

        "&:hover": {
            borderColor: "#9ca3af",
        },

        "&$disabled": {
            pointerEvents: "none",
            opacity: "0.6",
        },
    },
});

export default function TextInput({
    type,
    value,
    onChange,
    onFocus,
    onBlur,
    label,
    placeholder,
    helperText,
    name,
    disabled,
    isRequired,
    id,
    className,
    style,
}) {
    const classes = useStyles();
    const [isFocused, setIsFocused] = useState(false);
    const [localValue, setLocalValue] = useState("");
    const uniqueId = `textInput_${uuidv4()}`;

    if (type && !allowedInputTypes.includes(type)) {
        throw new Error(
            `Invalid type "${type}" passed to Input. Allowed input types are: ${allowedInputTypes.join(", ")}`
        );
    }

    useEffect(() => {
        if (!onChange) {
            console.warn('Warning: No "onChange" provided to TextInput component.');
        }
    }, []);

    const handleChange = (e) => {
        if (disabled) return;
        onChange ? onChange(e) : setLocalValue(e.target.value);
    };

    const handleFocus = (e) => {
        setIsFocused(true);
        onFocus && onFocus(e);
    };

    const handleBlur = (e) => {
        setIsFocused(false);
        onBlur && onBlur(e);
    };

    return (
        <FormControl className={`${classes.formControl} ${className ?? ""}`} style={style}>
            <label
                htmlFor={id || uniqueId}
                className={`${classes.inputLabel} ${value || localValue || isFocused ? "inputFilled" : ""}`}
            >
                {label}
                {label && isRequired && (
                    <span className={classes.requiredMark}>
                        <svg
                            stroke="currentColor"
                            fill="currentColor"
                            viewBox="0 0 480 512"
                            xmlns="http://www.w3.org/2000/svg"
                        >
                            <path d="M471.99 334.43L336.06 256l135.93-78.43c7.66-4.42 10.28-14.2 5.86-21.86l-32.02-55.43c-4.42-7.65-14.21-10.28-21.87-5.86l-135.93 78.43V16c0-8.84-7.17-16-16.01-16h-64.04c-8.84 0-16.01 7.16-16.01 16v156.86L56.04 94.43c-7.66-4.42-17.45-1.79-21.87 5.86L2.15 155.71c-4.42 7.65-1.8 17.44 5.86 21.86L143.94 256 8.01 334.43c-7.66 4.42-10.28 14.21-5.86 21.86l32.02 55.43c4.42 7.65 14.21 10.27 21.87 5.86l135.93-78.43V496c0 8.84 7.17 16 16.01 16h64.04c8.84 0 16.01-7.16 16.01-16V339.14l135.93 78.43c7.66-4.42 17.45-1.8 21.87-5.86l32.02-55.43c4.42-7.65 1.8-17.43-5.86-21.85z"></path>
                        </svg>
                    </span>
                )}
            </label>
            <input
                className={`${classes.inputDisplay} ${disabled ? classes.disabled : ""}`}
                type={type ?? "text"}
                value={value || localValue}
                onChange={handleChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                placeholder={placeholder}
                name={name}
                id={id || uniqueId}
                disabled={disabled}
            />
            {helperText && <p style={{ color: "red" }}>{helperText}</p>}
        </FormControl>
    );
}

TextInput.propTypes = {
    type: PropTypes.oneOf(allowedInputTypes),
    value: PropTypes.string,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    label: PropTypes.string,
    placeholder: PropTypes.string,
    name: PropTypes.string,
    disabled: PropTypes.bool,
    isRequired: PropTypes.bool,
    id: PropTypes.string,
    className: PropTypes.string,
};
